import { useState, useMemo } from "react";
import { DatePicker } from "rsuite";
import { AllowedFilterTypes, MicroLessonFilter, SearchProps } from "../types";

export const Search = ({ filters, setFilters, isLoading }: SearchProps) => {
  const [value, setValue] = useState("");
  const [type, setType] = useState<AllowedFilterTypes>("name");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const isSingleFiltered = filters.some((filter) => filter.type === "name" || filter.type === "lessonId");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!value || value.trim() === "" || isSingleFiltered) return;

    const filter = { type, value };
    setFilters([...filters, filter]);
    setValue("");
  };

  const handleRemoveFilter = (filter: MicroLessonFilter) => {
    setFilters(filters.filter((f) => f !== filter));
  };

  const handleDateChange = (date: Date | null, key: "from" | "to") => {
    if (key === "from") {
      setFromDate(date);
    } else if (key === "to") {
      setToDate(date);
    }

    const dateFilter = filters.find((f) => f.type === key);
    if (!dateFilter && !date) return;
    if (dateFilter && !date) {
      setFilters(filters.filter((f) => f.type !== key));
      return;
    }

    const filterValue = { type: key, value: date ? `${key}: ${date.toISOString().split("T")[0]}` : "" };

    if (dateFilter && date) {
      setFilters(filters.map((item) => (item.type === key ? filterValue : item)));
      return;
    }

    if (!dateFilter && date) {
      setFilters([...filters, filterValue]);
    }
  };

  const minDate = useMemo(() => new Date("2020-01-01"), []);
  const activeFilters = filters.filter((f) => f.type !== "from" && f.type !== "to");

  return (
    <div className="search-container">
      <div className="search-header">
        <p>Lesson Search Filters</p>
        {activeFilters.length > 0 && (
          <button className="secondary" onClick={() => setFilters([])}>
            Clear all filters
          </button>
        )}
      </div>
      <div className="search-input-wrapper">
        <form className="search-input-container" onSubmit={handleSubmit}>
          <input
            disabled={isLoading}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            name="value"
            type="text"
            placeholder="Enter Keyword"
          />
          <div className="select-wrapper">
            <select
              value={type}
              onChange={(e) => setType(e.target.value as AllowedFilterTypes)}
              name="type"
              defaultValue="name"
            >
              <option value="name">Lesson Name</option>
              <option value="lessonId">Lesson ID</option>
              <option value="objectives">Objective</option>
              <option value="tags">Tags</option>
            </select>
          </div>
          <button disabled={isSingleFiltered} type="submit" className="primary">
            Add
          </button>
        </form>
        <div className="date-picker-wrapper">
          <h2>Published Date Range</h2>
          <DatePicker
            oneTap
            value={fromDate}
            placeholder="From:"
            shouldDisableDate={(date) => {
              if (!date) return false;
              if (toDate) {
                return date > toDate || date < minDate;
              }
              return date < minDate;
            }}
            onChange={(date) => handleDateChange(date, "from")}
          />
          <DatePicker
            oneTap
            value={toDate}
            placeholder="To:"
            shouldDisableDate={(date) => {
              if (!date) return false;
              if (fromDate) {
                return date < fromDate || date < minDate;
              }
              return date < minDate;
            }}
            onChange={(date) => handleDateChange(date, "to")}
          />
        </div>
      </div>
      {activeFilters.length > 0 && (
        <div className="search-filters-wrapper">
          {activeFilters.map((filter) => (
            <div className="search-filter-item" key={filter.value}>
              <span>{filter.value}</span>
              <button onClick={() => handleRemoveFilter(filter)}>x</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
